import { useEffect, useMemo, useState, type PropsWithChildren } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Home,
  LayoutGrid,
  Lock,
  LogOut,
  MoreHorizontal,
  Plus,
  ShieldCheck,
  SquareActivity,
  User,
  Users,
} from 'lucide-react';
import clsx from 'clsx';
import type { TeamView, UserView } from '@retro/shared';
import { api, type RetroSummary } from '../lib/api.js';
import { useLogout } from '../lib/auth.js';
import { useTeams } from '../lib/teams.js';
import { Avatar, Modal } from './ui.js';

export function AppLogo({ compact = false }: { compact?: boolean }) {
  return (
    <Link to="/" className="flex items-center gap-2.5 font-bold tracking-[-0.02em]">
      <span className="grid size-8 flex-none place-items-center rounded-[9px] bg-navy text-[13px] font-extrabold tracking-normal text-white">
        r.
      </span>
      <span className={compact ? 'sr-only' : undefined}>
        <span className="block text-[15px] leading-none text-ink">retro.blue</span>
        <span className="mt-1 block text-[10px] font-semibold tracking-[0.04em] text-muted">
          by team.blue · internal
        </span>
      </span>
    </Link>
  );
}

/** The team's currently open retro, if any — feeds "Live now" and the Session tab. */
export function useLiveRetro(team: TeamView | null): RetroSummary | null {
  const retros = useQuery({
    queryKey: ['retros', team?.id],
    queryFn: () => api.teams.retros(team!.id),
    enabled: Boolean(team),
  });
  return useMemo(
    () => (retros.data?.retros ?? []).find((retro) => retro.phase !== 'CLOSED') ?? null,
    [retros.data],
  );
}

function NavItem({
  to,
  icon: Icon,
  label,
  active,
}: {
  to: string;
  icon: typeof Home;
  label: string;
  active: boolean;
}) {
  return (
    <Link
      to={to}
      aria-current={active ? 'page' : undefined}
      className={clsx(
        'flex min-h-[40px] items-center gap-2.5 rounded-lg px-2.5 text-[13px] font-semibold transition-colors',
        active ? 'bg-brand-100 text-brand-700' : 'text-ink2 hover:bg-subtle',
      )}
    >
      <Icon size={17} strokeWidth={active ? 2.2 : 1.8} aria-hidden className="flex-none" />
      <span className="truncate">{label}</span>
    </Link>
  );
}

export function AppShell({ user, crumb, children }: PropsWithChildren<{ user: UserView; crumb: string }>) {
  const { teams, activeTeam, selectTeam } = useTeams();
  const location = useLocation();
  const navigate = useNavigate();
  const logout = useLogout();
  const liveRetro = useLiveRetro(activeTeam);
  const [moreOpen, setMoreOpen] = useState(false);

  // Team-neutral aliases — TeamRedirect forwards to the active team once the
  // teams query resolves, so these links are stable even while it is loading.
  const teamRoot = '/team';
  const formatsPath = '/formats';

  /**
   * Switching team from the sidebar. On a team-scoped page the URL must move
   * with the selection — TeamPage and FormatsPage read their team from
   * useParams and re-sync the select to it, so selection alone reverts the
   * choice a moment later (the reported "flickers but stays"). The page kind
   * is preserved; a formats subpath is collapsed to the new team's formats
   * root, because templates are team-scoped and the old id's subpath does not
   * exist there. Team-neutral pages (overview, preferences, admin) keep the
   * dashboard's selection-only behaviour: the overview is reactive on
   * `activeTeam` and the user did not ask to leave it.
   */
  function switchTeam(id: string) {
    if (id === activeTeam?.id) return;
    selectTeam(id);
    const onTeamPage = /^\/teams\/[^/]+(.*)$/.exec(location.pathname);
    if (!onTeamPage) return;
    const suffix = onTeamPage[1] ?? '';
    navigate(suffix.startsWith('/formats') ? `/teams/${id}/formats` : `/teams/${id}`);
  }

  const nav = [
    { to: '/', icon: Home, label: 'Overview', active: location.pathname === '/' },
    {
      to: formatsPath,
      icon: LayoutGrid,
      label: 'Formats',
      active: activeTeam != null && location.pathname.startsWith(`/teams/${activeTeam.id}/formats`),
    },
    {
      to: teamRoot,
      icon: Users,
      label: 'Team hub',
      active: activeTeam != null && location.pathname === `/teams/${activeTeam.id}`,
    },
    { to: '/profile', icon: User, label: 'Preferences', active: location.pathname === '/profile' },
  ];
  const adminItem = user.isAdmin
    ? { to: '/admin', icon: ShieldCheck, label: 'Administration', active: location.pathname === '/admin' }
    : null;

  type MobileTab = 'home' | 'formats' | 'session' | 'team' | 'more';
  const activeTab: MobileTab =
    location.pathname === '/'
      ? 'home'
      : activeTeam && location.pathname.startsWith(`/teams/${activeTeam.id}/formats`)
        ? 'formats'
        : location.pathname.startsWith('/retro/')
          ? 'session'
          : activeTeam && location.pathname === `/teams/${activeTeam.id}`
            ? 'team'
            : 'more';

  function mobileGo(tab: MobileTab) {
    setMoreOpen(false);
    switch (tab) {
      case 'home':
        navigate('/');
        break;
      case 'formats':
        navigate(formatsPath);
        break;
      case 'session':
        if (liveRetro) navigate(`/retro/${liveRetro.id}`);
        else navigate(teamRoot);
        break;
      case 'team':
        navigate(teamRoot);
        break;
      case 'more':
        setMoreOpen(true);
        break;
    }
  }

  // Close the More sheet whenever navigation lands elsewhere.
  useEffect(() => setMoreOpen(false), [location.pathname]);

  const tabs: { id: MobileTab; label: string; icon: typeof Home; live?: boolean }[] = [
    { id: 'home', label: 'Home', icon: Home },
    { id: 'formats', label: 'Formats', icon: LayoutGrid },
    { id: 'session', label: 'Session', icon: SquareActivity, live: Boolean(liveRetro) },
    { id: 'team', label: 'Team', icon: Users },
    { id: 'more', label: 'More', icon: MoreHorizontal },
  ];

  return (
    <div className="min-h-dvh">
      {/* ------------------------------------------------ Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-[244px] flex-col gap-4 overflow-x-hidden overflow-y-auto border-r border-line bg-white px-3.5 pb-5 pt-5 lg:flex">
        <div className="px-1.5">
          <AppLogo />
        </div>

        {teams.length ? (
          <div className="px-1.5">
            <label className="text-[13px] font-semibold" htmlFor="workspace">
              Team
            </label>
            <select
              id="workspace"
              className="input mt-1.5 min-h-10 text-[12px] font-semibold"
              value={activeTeam?.id ?? ''}
              onChange={(event) => switchTeam(event.target.value)}
            >
              {teams.map((team) => (
                <option key={team.id} value={team.id}>
                  {team.name}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        <nav className="grid gap-0.5 px-1.5" aria-label="Primary">
          {[...nav, ...(adminItem ? [adminItem] : [])].map((item) => (
            <NavItem key={item.label} {...item} />
          ))}
        </nav>

        {liveRetro ? (
          <Link
            to={`/retro/${liveRetro.id}`}
            className="mx-1.5 flex items-center gap-2.5 rounded-[10px] border border-brand-200 bg-brand-100 px-3 py-2.5 text-brand-700 transition hover:bg-[#cfe4ff]"
          >
            <span className="relative flex size-2 flex-none">
              <span className="absolute inline-flex size-2 animate-ping rounded-full bg-[#12b76a] opacity-75" />
              <span className="relative inline-flex size-2 rounded-full bg-[#12b76a]" />
            </span>
            <span className="min-w-0">
              <strong className="block truncate text-[13px]">Live now</strong>
              <span className="block truncate text-[12px] text-ink2">{liveRetro.name}</span>
            </span>
          </Link>
        ) : null}

        <div className="mt-auto grid gap-3">
          <span className="inline-flex w-max items-center gap-1.5 rounded-md bg-subtle px-[9px] py-[5px] text-[11px] font-bold text-ink2">
            <Lock size={11} aria-hidden /> Internal · employees only
          </span>
          <div className="flex items-center gap-2.5 border-t border-line pt-3">
            <Link
              to="/profile"
              aria-label="Your profile"
              className="flex min-w-0 flex-1 items-center gap-2.5 rounded-[10px] p-1 -m-1 hover:bg-black/[0.04]"
            >
              <Avatar emoji={user.avatar} name={user.name} />
              <span className="min-w-0 flex-1">
                <strong className="block truncate text-[13px]">{user.name}</strong>
                <span className="block truncate text-[11px] text-muted">
                  {activeTeam?.role === 'FACILITATOR' ? 'Facilitator' : 'Contributor'}
                </span>
              </span>
            </Link>
            <button
              type="button"
              onClick={() => logout.mutate()}
              aria-label="Sign out"
              title="Sign out"
              className="icon-btn"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </aside>

      {/* ------------------------------------------------------ Top bar */}
      <div className="min-w-0 lg:pl-[244px]">
        <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-line bg-white px-3.5 backdrop-blur lg:h-[58px] lg:bg-canvas/95 lg:px-8">
          <div className="lg:hidden">
            <AppLogo compact />
          </div>
          <strong className="min-w-0 flex-1 truncate text-[15px] lg:hidden">{crumb}</strong>
          <nav className="hidden text-[13px] text-muted lg:block" aria-label="Breadcrumb">
            {activeTeam ? <span>{activeTeam.name}</span> : null}
            {activeTeam ? <span className="mx-[7px]">/</span> : null}
            <b className="font-semibold text-ink">{crumb}</b>
          </nav>
          <span className="ml-auto inline-flex items-center gap-1.5 rounded-md bg-subtle px-[7px] py-1 text-[10px] font-bold text-ink2 lg:hidden">
            <Lock size={10} aria-hidden /> Internal
          </span>
          <Link to="/profile" className="lg:hidden" aria-label="Preferences">
            <Avatar emoji={user.avatar} name={user.name} size="sm" />
          </Link>
          {/* Desktop top-right is intentionally empty: the POC puts Help here. */}
        </header>
        <main className="mx-auto max-w-[1120px] px-3.5 pb-[92px] pt-4 lg:px-8 lg:pb-12 lg:pt-7">{children}</main>
      </div>

      {/* --------------------------------------------------- Mobile nav */}
      <nav
        className="fixed inset-x-0 bottom-0 z-50 border-t border-line bg-white pb-[env(safe-area-inset-bottom)] lg:hidden"
        aria-label="Mobile navigation"
      >
        <div className="grid h-16 grid-cols-5">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => mobileGo(tab.id)}
                className={clsx(
                  'relative flex flex-col items-center justify-center gap-[3px] text-[11px] font-semibold',
                  active ? 'text-brand-700' : 'text-muted',
                )}
                aria-current={active ? 'page' : undefined}
              >
                {active ? (
                  <span className="absolute left-[28%] right-[28%] top-0 h-[3px] rounded-b-[3px] bg-brand-500" />
                ) : null}
                <span className="relative">
                  <Icon size={20} strokeWidth={active ? 2.2 : 1.8} aria-hidden />
                  {tab.live ? (
                    <span className="absolute -right-1.5 -top-0.5 size-[9px] rounded-full border-2 border-white bg-[#12b76a]" />
                  ) : null}
                </span>
                {tab.label}
              </button>
            );
          })}
        </div>
      </nav>

      {/* ------------------------------------------------------- More sheet */}
      <Modal open={moreOpen} onClose={() => setMoreOpen(false)} title="More">
        <ul className="menu-list">
          <li>
            <Link to="/profile" aria-current={location.pathname === '/profile' ? 'page' : undefined}>
              <User size={18} aria-hidden /> Preferences
            </Link>
          </li>
          {user.isAdmin ? (
            <li>
              <Link to="/admin" aria-current={location.pathname === '/admin' ? 'page' : undefined}>
                <ShieldCheck size={18} aria-hidden /> Administration
              </Link>
            </li>
          ) : null}
          {teams.length ? (
            <li>
              <button type="button" onClick={() => { setMoreOpen(false); navigate('/'); }}>
                <Plus size={18} aria-hidden /> Switch team
              </button>
            </li>
          ) : null}
          <li>
            <button type="button" onClick={() => logout.mutate()}>
              <LogOut size={18} aria-hidden /> Sign out
            </button>
          </li>
        </ul>
      </Modal>
    </div>
  );
}
